import { useState, useEffect } from 'react';
import { ListOrdered, CircleMinus, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { AbiParameter } from 'viem';
import {
  TransactionBuilderParameterInputProps,
  TransactionBuilderParameterState,
} from './types';
import {
  getArrayChildType,
  getDefaultFieldStateForParameterType,
  getParameterStateForArray,
  getStaticArrayLength,
} from './utils';
import { ParameterInput } from './ParameterInput';
import { getReadableSolidityName } from '@/lib/solidity-formatting';

export function ArrayInput({
  value,
  name,
  onChange,
  paramAbiFragment,
  networkId,
  allowUnsavedAddress = false,
  addressBookEntryByAddress,
  addressBookLookupPending = false,
  addressBookLookupFailed = false,
  cardLayout = false,
}: TransactionBuilderParameterInputProps): React.ReactElement {
  /**
   * Solidity type string that the array items conform to.
   */
  const arrayItemType = getArrayChildType(paramAbiFragment.type);
  /**
   * Length of array if static, otherwise null.
   */
  const staticArrayLength = getStaticArrayLength(paramAbiFragment.type);

  /**
   * The state of each item in the array
   */
  const [arrayItems, setArrayItems] = useState<
    TransactionBuilderParameterState[]
  >(JSON.parse(value || '[]'));

  // Call `onChange` callback when `arrayItems` changes
  useEffect(() => {
    // Case: no `onChange` callback defined
    if (!onChange) return;

    const newFieldState: TransactionBuilderParameterState =
      getParameterStateForArray(arrayItems, paramAbiFragment.type);

    // Case: `onChange` callback defined
    // Call callback with new array state
    onChange(name, newFieldState);
  }, [arrayItems]);

  /**
   * `onChange` callback for the array item parameters
   *
   * Updates the `arrayItems` state to reflect changes in the array item parameters
   *
   * @param {string} parameterName Name of the array element parameter that changed
   * @param {TransactionBuilderParameterState} parameterState New state of the array element parameter that changed
   */
  function handleArrayItemOnChangeEvent(
    parameterName: string,
    newParameterState: TransactionBuilderParameterState
  ): void {
    const arrayIndex: number = getArrayIndexFromParameterName(parameterName);

    // Copy array items (need to copy so that react state will detect update and re-render)
    const newArrayItems: TransactionBuilderParameterState[] = [...arrayItems];

    // In-place update the state for the element
    newArrayItems.splice(arrayIndex, 1, newParameterState);

    // Update array items
    setArrayItems(newArrayItems);
  }

  /**
   * Event handler for the "Add item" `<Button>` click event
   *
   * Create a new item in the `arrayItems` state variable
   */
  function handleAddItemButtonOnClickEvent(): void {
    if (!arrayItemType) return;
    const childAbi: AbiParameter = {
      ...paramAbiFragment,
      type: arrayItemType,
      // preserve components for tuple arrays
      components: (
        paramAbiFragment as AbiParameter & {
          components?: AbiParameter[];
        }
      ).components,
    };

    // Set `arrayItems` state variable to a NEW array with new default field state object
    // appended, so that react re-renders as it'll see the array object instance changed
    setArrayItems([
      ...arrayItems,
      getDefaultFieldStateForParameterType(childAbi),
    ]);
  }

  /**
   * Removes item at `arrayIndex` from `arrayItems` state variable
   *
   * @param {number} arrayIndex Index of the array item to remove
   */
  function removeItemFromArray(arrayIndex: number): void {
    // Copy array items (need to copy so that react state will detect update and re-render)
    const newArrayItems: TransactionBuilderParameterState[] = [...arrayItems];

    // In-place remove the element
    newArrayItems.splice(arrayIndex, 1);

    // Update array items
    setArrayItems(newArrayItems);
  }

  // Case: unable to get the solidity type of the array children
  if (!arrayItemType) {
    return (
      <div className="text-sm text-red-500">
        Invalid array type: {paramAbiFragment.type}. If this is wrong, please
        contact the Den team.
      </div>
    );
  }

  // Case: able to get solidity type of array children
  const childAbi: AbiParameter = {
    ...paramAbiFragment,
    type: arrayItemType,
    components: (
      paramAbiFragment as AbiParameter & {
        components?: AbiParameter[];
      }
    ).components,
  };

  return (
    <div className="w-full">
      <fieldset
        className={cn(
          !cardLayout &&
            'rounded-[16px] border border-zinc-300 dark:border-zinc-700 p-4 bg-zinc-50/50 dark:bg-zinc-900/50'
        )}
      >
        <legend
          className={cn(
            'flex items-center gap-1.5 px-2 text-sm font-medium text-zinc-900 dark:text-white',
            cardLayout && 'sr-only'
          )}
        >
          <ListOrdered className="h-4 w-4" />
          {getReadableSolidityName(
            paramAbiFragment.name || name,
            'Parameter'
          )}{' '}
          ({arrayItems.length} {arrayItems.length === 1 ? 'item' : 'items'})
        </legend>
        <div className={cn('flex flex-col gap-3', !cardLayout && 'pl-2')}>
          <div
            className={cn('flex flex-col', cardLayout ? 'divide-y' : 'gap-4')}
          >
            {/* Display inputs for each element */}
            {arrayItems.map(
              (item: TransactionBuilderParameterState, index: number) => (
                <div
                  key={index}
                  className={cn(
                    'flex items-start gap-2',
                    cardLayout
                      ? 'py-2'
                      : 'rounded-[12px] border border-zinc-200 dark:border-zinc-800 p-3 bg-white dark:bg-zinc-950'
                  )}
                >
                  <div className="flex-1">
                    <ParameterInput
                      cardLayout={cardLayout}
                      name={getParameterNameFromArrayIndex(
                        name || 'param',
                        index
                      )}
                      value={item.value}
                      unformattedValue={(item.parsedValue as string) || ''}
                      isValid={item.isValid}
                      isEmpty={item.isEmpty}
                      networkId={networkId}
                      paramAbiFragment={childAbi}
                      onChange={handleArrayItemOnChangeEvent}
                      allowUnsavedAddress={allowUnsavedAddress}
                      addressBookLookupPending={addressBookLookupPending}
                      addressBookLookupFailed={addressBookLookupFailed}
                      {...(addressBookEntryByAddress !== undefined
                        ? { addressBookEntryByAddress }
                        : {})}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItemFromArray(index)}
                    className={cn(
                      'p-1 transition-colors',
                      cardLayout
                        ? 'text-muted-foreground hover:text-destructive'
                        : 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-500'
                    )}
                    aria-label="Remove item"
                  >
                    {cardLayout ? (
                      <X className="size-4" />
                    ) : (
                      <CircleMinus className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )
            )}
            {arrayItems.length === 0 && (
              <div className="text-sm text-zinc-500 dark:text-zinc-400">
                No items added
              </div>
            )}
            {staticArrayLength !== null &&
              arrayItems.length !== staticArrayLength && (
                <div className="text-xs text-amber-600 dark:text-amber-400">
                  Must have {staticArrayLength} items
                </div>
              )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddItemButtonOnClickEvent}
            className={cn(
              cardLayout &&
                'rounded-full bg-neutral-50 text-xs text-muted-foreground'
            )}
          >
            {cardLayout && <Plus className="size-4" />}
            Add item
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

/**
 * Constructs the parameter name for an array item based on its index
 * E.g. "orders-item-1"
 *
 * @param {string} arrayName Name of the array parameter
 * @param {number} arrayIndex Index of the array item
 * @returns {string} The parameter name for the array item at `arrayIndex`
 */
function getParameterNameFromArrayIndex(
  arrayName: string,
  arrayIndex: number
): string {
  return `${arrayName}-item-${arrayIndex + 1}`;
}

/**
 * Gets the array index from the parameter name of an array item generated by
 * the `getParameterNameFromArrayIndex` function
 *
 * @param {string} parameterName Parameter name of the array item generated by
 *                                the `getParameterNameFromArrayIndex` function
 *                                (e.g. "orders-item-1")
 * @returns {number} The array index of the array item
 */
function getArrayIndexFromParameterName(parameterName: string): number {
  const parts = parameterName.split('-item-');
  const indexStr = parts[1] || '1';
  return parseInt(indexStr) - 1;
}
